/**
 * ServicePricingPage
 * Main pricing page showing service tiers with registration CTA
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  useTheme,
  alpha,
  Chip,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from '@mui/material';
import {
  CheckCircle,
  Star,
  Rocket,
  Phone,
  ArrowForward,
  Business,
  Security,
  Speed,
  Support,
  TrendingUp,
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { SERVICE_TIERS, ServiceTier, formatPrice } from '../lib/serviceTiers';
import { useAuth } from '../contexts/AuthContext';

const ServicePricingPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showAnnual, setShowAnnual] = useState(false);

  const handleGetStarted = (tierId: string) => {
    navigate(`/retainer-checkout?tier=${tierId}`);
  };

  const renderTierCard = (tier: ServiceTier) => {
    const isEnterprise = tier.enterprise;
    const monthlyDisplay = showAnnual && tier.monthlyFee > 0
      ? Math.round(tier.monthlyFee * 0.83) // ~17% discount for annual
      : tier.monthlyFee;

    return (
      <Card
        key={tier.id}
        sx={{
          height: '100%',
          position: 'relative',
          border: tier.recommended ? '2px solid' : '1px solid',
          borderColor: tier.recommended ? 'primary.main' : 'divider',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: 8,
          },
        }}
      >
        {tier.recommended && (
          <Chip
            label="MOST POPULAR"
            color="primary"
            icon={<Star sx={{ fontSize: 16 }} />}
            sx={{
              position: 'absolute',
              top: -14,
              left: '50%',
              transform: 'translateX(-50%)',
              fontWeight: 700,
              px: 1,
            }}
          />
        )}

        <CardContent sx={{ p: 4 }}>
          {/* Tier Name & Tagline */}
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {tier.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {tier.tagline}
          </Typography>

          {/* Pricing */}
          <Box mb={3}>
            <Typography variant="h3" fontWeight={800} color="primary.main">
              {formatPrice(tier.retainerFee)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              one-time setup fee
            </Typography>
          </Box>

          {tier.monthlyFee > 0 ? (
            <Box mb={3}>
              <Box display="flex" alignItems="baseline" gap={1}>
                <Typography variant="h5" fontWeight={700}>
                  + {formatPrice(monthlyDisplay)}
                </Typography>
                <Typography color="text.secondary">/mo</Typography>
              </Box>
              {showAnnual && (
                <Typography variant="body2" color="success.main">
                  Save 17% with annual billing
                </Typography>
              )}
            </Box>
          ) : isEnterprise ? (
            <Box mb={3}>
              <Typography variant="h6" color="text.secondary">
                Custom monthly pricing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contact us for a quote
              </Typography>
            </Box>
          ) : null}

          {/* CTA Button */}
          <Button
            variant={tier.recommended ? 'contained' : 'outlined'}
            fullWidth
            size="large"
            onClick={() => handleGetStarted(tier.id)}
            endIcon={<ArrowForward />}
            sx={{
              py: 1.5,
              mb: 3,
              ...(tier.recommended && {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }),
            }}
          >
            {isEnterprise ? 'Request Enterprise' : 'Request Service'}
          </Button>

          <Divider sx={{ mb: 2 }} />

          {/* Features List */}
          <List dense disablePadding>
            {tier.features.slice(0, 8).map((feature, idx) => (
              <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary={feature}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
            {tier.features.length > 8 && (
              <Typography variant="body2" color="primary" sx={{ mt: 1, pl: 3.5 }}>
                + {tier.features.length - 8} more features
              </Typography>
            )}
          </List>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0A0E27' }}>
      {/* Header */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'transparent', backdropFilter: 'blur(10px)' }}>
        <Toolbar>
          <Typography
            variant="h5"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              fontWeight: 900,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            REDEX LABS
          </Typography>

          <Button
            component={Link}
            to="/contact"
            startIcon={<Phone />}
            sx={{ color: 'white', mr: 2 }}
          >
            Talk to Sales
          </Button>

          {isAuthenticated ? (
            <Button
              component={Link}
              to="/dashboard"
              variant="outlined"
              sx={{ borderColor: alpha('#fff', 0.3), color: 'white' }}
            >
              Dashboard
            </Button>
          ) : (
            <Button
              component={Link}
              to="/login"
              variant="outlined"
              sx={{ borderColor: alpha('#fff', 0.3), color: 'white' }}
            >
              Sign In
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          py: 10,
          textAlign: 'center',
          background: 'linear-gradient(180deg, #0A0E27 0%, #1a1f3c 100%)',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            fontWeight={900}
            sx={{
              mb: 2,
              background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Scale Your Pipeline
          </Typography>
          <Typography variant="h5" color="grey.400" sx={{ mb: 4 }}>
            Qualified B2B leads delivered weekly. Full-service CRM setup included.
          </Typography>

          {/* Billing Toggle */}
          <Box display="flex" justifyContent="center" alignItems="center" gap={2} mb={4}>
            <Typography color={showAnnual ? 'grey.500' : 'white'}>Monthly</Typography>
            <ToggleButtonGroup
              value={showAnnual ? 'annual' : 'monthly'}
              exclusive
              onChange={(_, val) => setShowAnnual(val === 'annual')}
              sx={{
                '& .MuiToggleButton-root': {
                  color: 'grey.400',
                  borderColor: alpha('#fff', 0.2),
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                  },
                },
              }}
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="annual">Annual</ToggleButton>
            </ToggleButtonGroup>
            <Typography color={showAnnual ? 'white' : 'grey.500'}>
              Annual
              <Chip label="Save 17%" size="small" color="success" sx={{ ml: 1 }} />
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Pricing Cards */}
      <Container maxWidth="xl" sx={{ py: 8, mt: -6 }}>
        <Grid container spacing={3} justifyContent="center">
          {SERVICE_TIERS.map((tier) => (
            <Grid item xs={12} sm={6} lg={3} key={tier.id}>
              {renderTierCard(tier)}
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Trust Badges */}
      <Box sx={{ py: 6, bgcolor: alpha('#fff', 0.02) }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {[
              { icon: <Security />, label: 'SOC2 Compliant', desc: 'Enterprise security' },
              { icon: <Speed />, label: '99.9% Uptime', desc: 'SLA guaranteed' },
              { icon: <Support />, label: '24/7 Support', desc: 'Always available' },
              { icon: <TrendingUp />, label: '500+ Clients', desc: 'Trusted by leaders' },
            ].map((badge, idx) => (
              <Grid item xs={6} md={3} key={idx}>
                <Box textAlign="center">
                  <Box sx={{ color: 'primary.main', mb: 1 }}>{badge.icon}</Box>
                  <Typography variant="subtitle1" fontWeight={700} color="white">
                    {badge.label}
                  </Typography>
                  <Typography variant="body2" color="grey.500">
                    {badge.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ / What's Included */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" color="white" mb={6}>
          What's Included in Every Plan
        </Typography>

        <Grid container spacing={3}>
          {[
            {
              icon: <Business />,
              title: 'Full CRM Setup',
              desc: 'Complete CRM configuration, integrations, and training for your team.',
            },
            {
              icon: <Rocket />,
              title: 'Multi-Channel Outreach',
              desc: 'Email, SMS, phone, and LinkedIn outreach on autopilot.',
            },
            {
              icon: <Security />,
              title: 'Enterprise Security',
              desc: 'SSL, encrypted data, SOC2 compliance, and GDPR ready.',
            },
            {
              icon: <Support />,
              title: 'Dedicated Support',
              desc: 'Priority support from our team of experts.',
            },
          ].map((item, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Paper
                sx={{
                  p: 3,
                  bgcolor: alpha('#fff', 0.03),
                  border: '1px solid',
                  borderColor: alpha('#fff', 0.1),
                }}
              >
                <Box display="flex" gap={2}>
                  <Box sx={{ color: 'primary.main' }}>{item.icon}</Box>
                  <Box>
                    <Typography variant="h6" fontWeight={600} color="white">
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="grey.400">
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          py: 8,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} color="white" gutterBottom>
            Ready to Scale Your Business?
          </Typography>
          <Typography variant="body1" color="white" sx={{ opacity: 0.9, mb: 4 }}>
            Join 500+ companies using REDEX to generate qualified leads
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/retainer-checkout')}
              sx={{
                bgcolor: 'white',
                color: '#764ba2',
                px: 4,
                '&:hover': { bgcolor: alpha('#fff', 0.9) },
              }}
            >
              Request Service
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/contact"
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                '&:hover': { bgcolor: alpha('#fff', 0.1) },
              }}
            >
              Talk to Sales
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, borderTop: `1px solid ${alpha('#fff', 0.1)}` }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="grey.500" textAlign="center">
            © {new Date().getFullYear()} REDEX Labs. All rights reserved. |{' '}
            <Link to="/terms" style={{ color: 'inherit' }}>Terms</Link> |{' '}
            <Link to="/privacy" style={{ color: 'inherit' }}>Privacy</Link>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default ServicePricingPage;
